import type { Counterparty, CounterpartyActivity, CounterpartyMapWithLinks } from '@gcv/shared';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { BanksApi } from 'api';
import {
  BANK_COUNTERPARTIES_KEY,
  COUNTERPARTY_ACTIVITY_KEY,
  CRB_COUNTERPARTIES_KEY
} from 'tanstack-hooks/keys';
import { useUser } from 'tanstack-hooks/user';

export function useCreateCounterpartyActivity() {
  const { userBankId } = useUser();
  const banksApi = new BanksApi();
  const queryClient = useQueryClient();

  const { mutateAsync: createCounterpartyActivity, isPending: isCreatingCounterpartyActivity } = useMutation({
    mutationKey: [COUNTERPARTY_ACTIVITY_KEY],
    mutationFn: async (activities: Partial<CounterpartyActivity>[]) => {
      return await banksApi.upsertCounterpartyActivity(userBankId, activities as CounterpartyActivity[]);
    },
    onSuccess: (createdActivities) => {
      // Optimistically update the activity cache so reopening the drawer shows the new association immediately
      queryClient.setQueriesData(
        { queryKey: [COUNTERPARTY_ACTIVITY_KEY] },
        (old: CounterpartyActivity[] | undefined) => {
          if (!old) return createdActivities as CounterpartyActivity[];
          const createdIds = new Set(createdActivities.map((a) => a.id));
          return [
            ...old.filter((a) => !createdIds.has(a.id)),
            ...(createdActivities as CounterpartyActivity[])
          ];
        }
      );
      queryClient.invalidateQueries({ queryKey: [COUNTERPARTY_ACTIVITY_KEY] });

      const bankCounterpartyEntries = queryClient.getQueriesData<CounterpartyMapWithLinks>({
        queryKey: [BANK_COUNTERPARTIES_KEY]
      });
      const bankCounterpartyMap = bankCounterpartyEntries[0]?.[1];

      if (bankCounterpartyMap) {
        queryClient.setQueriesData(
          { queryKey: [CRB_COUNTERPARTIES_KEY] },
          (old: { [crbId: string]: { [counterpartyId: string]: Counterparty } } | undefined) => {
            if (!old) return old;
            const updated = { ...old };
            for (const activity of createdActivities) {
              const crbId = activity.org_id;
              const cpId = activity.counterparty_id;
              const cp = bankCounterpartyMap[cpId]?.counterparty;
              if (crbId && cpId && cp) {
                updated[crbId] = { ...updated[crbId], [cpId]: cp };
              }
            }
            return updated;
          }
        );
      }
    },
    meta: {
      snackbarSuccessMessage: 'Counterparty associated successfully.',
      snackbarErrorMessage:
        'There was an issue associating the counterparty. Please try again, or contact support if the problem persists.'
    }
  });

  return { createCounterpartyActivity, isCreatingCounterpartyActivity };
}
